import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getMarkMeta } from "./clinicalFormMarks";
import { displayDateTimeUS } from "../../../../utils/dateUtils";
import { UserIcon, CalendarIcon } from "../../../../Icons";

const TIP_WIDTH = 228;

/** Distinct icon badge + colored hover card. Optional Remove for Admin/OA. */
const ClinicalFormMarkBadge = ({
  mark,
  size = "md",
  onRemove,
  removing = false,
}) => {
  const meta = getMarkMeta(mark.markType || mark.type);
  const label = mark.label || meta.label;
  const color = mark.color || meta.color;
  const Icon = meta.Icon;
  const byName = (mark.markedByName || "").trim() || "—";
  const when = displayDateTimeUS(mark.markedAt) || "—";
  const box = size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const iconSize = size === "sm" ? 11 : 13;
  const tipId = useId();
  const canRemove = typeof onRemove === "function";

  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: true });
  const closeTimer = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const place = () => {
      const el = btnRef.current;
      const tip = tipRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const tipH = tip?.offsetHeight || 112;
      const gap = 8;
      const placeAbove = rect.top > tipH + gap + 16;
      let left = rect.left + rect.width / 2 - TIP_WIDTH / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - TIP_WIDTH - 8));
      const top = placeAbove ? rect.top - tipH - gap : rect.bottom + gap;
      setCoords({ top, left, placeAbove });
    };

    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, when, byName, label, canRemove]);

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = () => {
    clearClose();
    setOpen(true);
  };
  const hideSoon = () => {
    clearClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const hide = () => {
    clearClose();
    setOpen(false);
  };

  const tooltip =
    open &&
    createPortal(
      <div
        ref={tipRef}
        id={tipId}
        role="tooltip"
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          width: TIP_WIDTH,
          zIndex: 90,
        }}
        className={`relative ${canRemove ? "pointer-events-auto" : "pointer-events-none"}`}
        onMouseEnter={canRemove ? show : undefined}
        onMouseLeave={canRemove ? hideSoon : undefined}
      >
        <div
          className="rounded-lg border bg-white shadow-xl overflow-hidden"
          style={{ borderColor: `${color}66` }}
        >
          <div
            className="flex items-center gap-2 px-2.5 py-2 text-white"
            style={{ backgroundColor: color }}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-white/20 shrink-0">
              {Icon ? <Icon size={13} strokeWidth={2.25} /> : meta.code}
            </span>
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-white/80 leading-none">
                Flag
              </p>
              <p className="text-xs font-semibold truncate mt-0.5">{label}</p>
            </div>
          </div>
          <div className="px-2.5 py-2 space-y-1.5 bg-slate-50">
            <div className="flex items-start gap-2">
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded mt-0.5 shrink-0"
                style={{ backgroundColor: `${color}22`, color }}
              >
                <UserIcon size={11} />
              </span>
              <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 leading-none">
                  Updated by
                </p>
                <p className="text-[11px] font-medium text-slate-800 truncate mt-0.5">
                  {byName}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded mt-0.5 shrink-0"
                style={{ backgroundColor: `${color}22`, color }}
              >
                <CalendarIcon size={11} />
              </span>
              <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 leading-none">
                  Date & time
                </p>
                <p className="text-[11px] font-medium text-slate-800 tabular-nums mt-0.5">
                  {when}
                </p>
              </div>
            </div>
            {canRemove ? (
              <button
                type="button"
                disabled={removing}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hide();
                  onRemove?.(mark.markType || mark.type);
                }}
                className="w-full mt-1 h-7 rounded-md border border-slate-200 bg-white text-[11px] font-semibold text-slate-700 hover:bg-slate-100 hover:border-slate-300 disabled:opacity-50"
              >
                {removing ? "Removing…" : `Remove ${label}`}
              </button>
            ) : null}
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={canRemove ? hideSoon : hide}
        onFocus={show}
        onBlur={canRemove ? hideSoon : hide}
        className={`inline-flex ${box} items-center justify-center rounded-md text-white shadow-sm shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
        style={{ backgroundColor: color, "--tw-ring-color": color }}
        aria-label={
          canRemove
            ? `${label}, by ${byName}. Hover to remove.`
            : `${label}, by ${byName}, ${when}`
        }
        aria-describedby={open ? tipId : undefined}
      >
        {Icon ? <Icon size={iconSize} strokeWidth={2.25} /> : meta.code}
      </button>
      {tooltip}
    </>
  );
};

export default ClinicalFormMarkBadge;
